import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../App';

export default function ManageTrustees() {
  const [trustees, setTrustees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [resetPw, setResetPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const { user } = useAuth();

  // Close menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handler = () => setOpenMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenu]);

  const loadTrustees = useCallback(async () => {
    try {
      const data = await api.getTrustees();
      setTrustees(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrustees(); }, [loadTrustees]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await api.createTrustee(form);
      setSuccess(`${form.name} has been added as a trustee.`);
      setForm({ name: '', email: '', password: '' });
      setShowForm(false);
      loadTrustees();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    setError('');
    try {
      await api.editTrustee(id, editForm);
      setEditing(null);
      loadTrustees();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPw || !newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      await api.resetTrusteePassword(resetPw.id, newPassword);
      setSuccess(`Password reset for ${resetPw.name}. Share the new password with them directly.`);
      setResetPw(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    try {
      await api.deleteTrustee(deleteTarget.id);
      setDeleteTarget(null);
      loadTrustees();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div className="page container">
      <div className="form-page">
        <div className="board-header">
          <h1 className="page-title">Trustees</h1>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Trustee'}
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {showForm && (
          <form className="admin-section glass" onSubmit={handleCreate}>
            <h3>New Trustee</h3>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              <p className="form-hint">At least 8 characters. Share this with the new trustee directly.</p>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create Trustee'}
            </button>
          </form>
        )}

        <div className="trustees-table">
          {trustees.map((t) => (
            <div key={t.id} className="trustee-row">
              {editing === t.id ? (
                <div className="trustee-edit">
                  <div className="trustee-edit-fields">
                    <div className="form-group">
                      <label className="form-label">Name</label>
                      <input className="form-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input className="form-input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} type="email" />
                    </div>
                  </div>
                  <div className="trustee-edit-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => handleEdit(t.id)}>Save</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="trustee-info">
                    <span className="trustee-name">{t.name}</span>
                    <span className="trustee-email">{t.email}</span>
                  </div>
                  {t.id !== user?.id && (
                    <div className="trustee-menu-wrap">
                      <button className="update-menu-trigger" onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === t.id ? null : t.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                      </button>
                      {openMenu === t.id && (
                        <div className="update-menu">
                          <button onClick={() => { setEditing(t.id); setEditForm({ name: t.name, email: t.email }); setOpenMenu(null); }}>Edit</button>
                          <button onClick={() => { setResetPw(t); setNewPassword(''); setOpenMenu(null); }}>Reset Password</button>
                          <button className="update-action-danger" onClick={() => { setDeleteTarget(t); setOpenMenu(null); }}>Remove</button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {resetPw && (
        <div className="modal-overlay" onClick={() => setResetPw(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reset Password</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', marginBottom: 'var(--space-md)' }}>
              Set a new password for <strong>{resetPw.name}</strong>. You'll need to share it with them directly.
            </p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 8 characters" minLength={8} autoFocus />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setResetPw(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword} disabled={newPassword.length < 8}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remove Trustee</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
              Remove <strong>{deleteTarget.name}</strong> as a trustee? They will lose access to the dashboard. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
